/**
 * @summary - Component that Provides hospital Information List
 * @author Yuvasri S
 * @since 2024-07-04
 */
import React, { useState, useEffect } from "react";
import { Box, Stack, Typography } from "@mui/material";
import CustomAgGrid from "../commoncomponents/CustomAgGrid";
import CustomDialog from "../commoncomponents/CustomDialog";
import { useNavigate } from "react-router-dom";
import { DefaultCard } from "../../assets/styled-components/card";
import {
  getAuthToken,
  getFacilityId,
  getcompanyId,
} from "../../utility/tokenUtility";
import axios from "axios";
import CreateNewButton from "../commoncomponents/CreateNewButton";
import RefreshExportButtons from "../commoncomponents/RefreshExportButtons";
import { FemsGraph, MasterGraph, ifacSecurityURL } from "../../utility/apiURLS";
import CloseButton from "../commoncomponents/CloseButton";
import HomeIconButton from "../commoncomponents/HomeIconButton";
import PageTitle from "../commoncomponents/PageTitle";
import SectionTitle from "../commoncomponents/SectionTitle";
import { useAccess } from "../../AccessContext";
import utilities from '../../utility/utilities';


const columns = [
  { field: "facilitycode", headerName: "Hospital Code" },
  { field: "facilityname", headerName: "Hospital Name" },
  { field: "hosptype", headerName: "Type" },
  { field: "directorname", headerName: "Director's Name" },
  { field: "address", headerName: "Address" },
  { field: "postcode", headerName: "Postal" },
  { field: "hospemail", headerName: "Email" },
  { field: "hosptelephoneno", headerName: "Telephone" },
];

const HospitalInformationList = () => {
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [deletedRowDetails, setDeletedRowDetails] = useState({});
  const navigate = useNavigate();
  const [gridApi, setGridApi] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const accessData = useAccess();
  const access = utilities.getScreenPermissions(accessData, "ENGM006");

  const token = getAuthToken("id_token");
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  const companyId = getcompanyId("companyId");
  const facilityId = getFacilityId("facilityId");

  const fetchStateData = async () => {
    const response = await axios.get(
      `${ifacSecurityURL}/api/secstatemasters`,
      config
    );
    const stateData = response.data;
    const stateMap = stateData.reduce((acc, state) => {
      acc[state.id] = state.statename;
      return acc;
    }, {});
    return stateMap;
  };
  const fetchHospitalInformation = async () => {
    setIsLoading(true);
    try {
      // Fetch the state data
      const stateMap = await fetchStateData();

      // Fetch the initial list of hospitals based on company ID
      const response1 = await axios.get(
        `${ifacSecurityURL}/api/GraphUserdetails/Locationbasedoncompantid/${companyId}?size=1000`,
        config
      );
      const hospitalData = response1.data;

      // Filter out hospitals without detailed information
      const hospitalDetails = await Promise.all(
        hospitalData.map(async (hospital) => {
          try {
            const response2 = await axios.get(
              `${MasterGraph}/api/GraphHospitalMaster/GetHospitalMasterByFacilityId/${hospital.id}`,
              config
            );
            const hospitalDetail = response2.data;

            if (!hospitalDetail || hospitalDetail.length === 0) {
              // If no details are found, skip this hospital
              return null;
            }

            // Ensure hospitalDetail is not nested inside an object
            const sanitizedHospitalDetail = hospitalDetail[0] || hospitalDetail;

            // Merge the data correctly at the top level and include hosptype and state name
            const combinedData = {
              hospitalid: hospital.id,
              facilityname: hospital.facilityname,
              facilitycode: hospital.facilitycode,
              ...sanitizedHospitalDetail,
              hosptype: sanitizedHospitalDetail.mashospitaltype?.hospType || "",
              state: stateMap[sanitizedHospitalDetail.stateid] || "",
            };


            return combinedData;
          } catch (error) {
            console.error(
              `Error fetching details for hospital ID ${hospital.id}:`,
              error
            );
            return null; // Skip the hospital if the second request fails
          }
        })
      );

      // Filter out any null values from the result
      const filteredHospitalDetails = hospitalDetails.filter(
        (detail) => detail !== null
      );

      setRows(filteredHospitalDetails);
      setFilteredRows(filteredHospitalDetails);
    } catch (error) {
      console.error("Error fetching hospital information:", error);
    }
    finally {
      setIsLoading(false);
    }
  };

  const deleteHospitalInformation = async (id) => {
    try {
      await axios.delete(`${MasterGraph}/api/mashospitaldetails/${id}`, config);
      fetchHospitalInformation();
    } catch (error) {
      console.error("Error deleting asset category:", error);
    }
  };

  useEffect(() => {
    fetchHospitalInformation();
  }, []);

  const handleDelete = (row) => {
    setRowToDelete(row);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (rowToDelete) {
      await deleteHospitalInformation(rowToDelete.id);
      setDeletedRowDetails({
        code: rowToDelete.assetcategory,
        desc: rowToDelete.assetcategorydesc,
      });
      setDeleteDialogOpen(false);
      setConfirmationDialogOpen(true);
      setRowToDelete(null); // Clear the row to delete after deletion
    }
  };

  const handleCreateHospitalInformation = () => {
    navigate("register");
  };

  const handleEdit = (rowData) => {
    navigate("edit", { state: { ...rowData, isEditMode: true } });
  };

  const handleView = (rowData) => {
    navigate("edit", { state: { ...rowData, isViewMode: true } });
  };

  const handleRefresh = () => {
    fetchHospitalInformation();
  };

  return (
    <>
      <PageTitle title="Hospital Information" />
      <DefaultCard>
        <Box
          mb={1}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
          }}
        >
          <SectionTitle title="Hospital Information List" />
          <Stack direction={"row"} spacing={1.5}>
            {access?.canCreate && <CreateNewButton onClick={handleCreateHospitalInformation} />}
            <RefreshExportButtons
              pagination={false}
              exportFunction={() => { }}
              exportData={filteredRows}
              refData={gridApi}
              handleRefresh={handleRefresh}
              title={"Hospital Information"}
            />
          </Stack>
        </Box>
        <CustomAgGrid
          rows={filteredRows}
          columns={columns}
          handleEdit={access?.canEdit &&handleEdit}
          handleDelete={access?.canDelete &&handleDelete}
          handleView={access?.canView &&handleView}
          setGridApi={setGridApi}
          isLoading={isLoading}
        />
        <CustomDialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          onConfirm={confirmDelete}
          title="Confirmation!"
          content="Are you sure you want to delete?"
          cancelBtn="No"
          confirmBtn="Yes"
        />
        <CustomDialog
          open={confirmationDialogOpen}
          onClose={() => setConfirmationDialogOpen(false)}
          title="Deletion Successful"
          content={<>Record Deleted Successfully</>}
          confirmBtn="OK"
          onConfirm={() => setConfirmationDialogOpen(false)}
        />
        <CloseButton />
      </DefaultCard>
    </>
  );
};

export default HospitalInformationList;
